import { CircularProgress } from "@mui/material";
import { Feature, FeatureCollection } from "geojson";
import {
  ArableField,
  getAllArableFieldGeoms,
  getArableFieldsCenterWithNoTilesForNotDeletedFieldBoundaries,
  getArableFieldsWithNoTilesForNotDeletedFieldBoundaries,
  RootState,
  selectAllArableFields,
  selectTmpDeletedFeatures,
} from "model";
import { MutableRefObject, useMemo } from "react";
import { Layer, MapRef, Marker, Source } from "react-map-gl";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import DiaryEntryLocationMarkerWithScroll from "./DiaryEntryLocationMarkerWithScroll";

interface Props {
  editedFieldId?: string;
  fieldId?: string;
  fieldIds?: string[];
  fillLayers?: boolean;
  mapRef: MutableRefObject<MapRef | null>;
  setTooltipOverMarker: (value: boolean) => void;
}

interface MatchProps {
  id: string;
}

export default function ArableFieldGeoJSONLayers({
  editedFieldId,
  fieldId,
  fieldIds,
  fillLayers,
  mapRef,
  setTooltipOverMarker,
}: Props) {
  const editMatch = useRouteMatch<MatchProps>("/edit-field/:id");
  const fieldGeoms = useSelector(getAllArableFieldGeoms);
  const currentArableFieldGeom = useSelector(
    (state: RootState) => fieldId && state.arableFields.entities[fieldId]?.geom
  );
  const tmpDeletedFeatures = useSelector(selectTmpDeletedFeatures);
  const arableFieldsWithNoTiles = useSelector(
    getArableFieldsWithNoTilesForNotDeletedFieldBoundaries
  ).filter((fieldGuid) => {
    // Always exclude edited field
    if (fieldGuid === editedFieldId) return false;

    // If fieldIds is provided, only include fields from that list
    if (fieldIds && fieldIds.length > 0) {
      return fieldIds.includes(fieldGuid);
    }

    // Otherwise include all fields
    return true;
  });

  const arableFieldsWithNoTilesCenters = useSelector(
    getArableFieldsCenterWithNoTilesForNotDeletedFieldBoundaries
  ).filter(
    (field) => field.guid !== editedFieldId && fieldIds?.includes(field.guid)
  ); // TODO: move to selector;

  const filteredFieldGeoms = useMemo(() => {
    // If no fieldIds provided, return all fields
    if (!fieldIds || fieldIds.length === 0) return fieldGeoms;

    // Otherwise filter to only show specified fields
    return {
      type: "FeatureCollection",
      features: fieldGeoms.features.filter((feature) =>
        fieldIds.includes(feature.properties?.id as string)
      ),
    };
  }, [fieldGeoms, fieldIds]);

  const zoom = mapRef.current?.getMap()?.getZoom();
  const allFields = useSelector(selectAllArableFields);

  return (
    <>
      <Source
        id="arable-field-outlines"
        data={filteredFieldGeoms as FeatureCollection}
        type="geojson"
      >
        <Layer
          id="arable-field-fill"
          type="fill"
          //filter out fill of field thats being edited
          filter={["!=", "id", editMatch?.params.id || ""]}
          paint={{
            "fill-opacity": fillLayers ? 1.0 : 0,
            "fill-color": fillLayers ? "#FADF75" : "transparent",
          }}
        />
        <Layer
          id="arable-field-fill-no-data"
          type="fill"
          //only show this if a field has no tiles
          filter={["in", ["get", "id"], ["literal", arableFieldsWithNoTiles]]}
          paint={{ "fill-color": "#666666" }}
        />
        <Layer
          id="arable-outline"
          type="line"
          paint={{
            "line-color": "#666666",
            "line-width": [
              "interpolate",
              ["exponential", 2],
              ["zoom"],
              10,
              ["*", 5, ["^", 2, -6]],
              24,
              ["*", 5, ["^", 2, 7]],
            ],
            "line-offset": [
              "interpolate",
              ["exponential", 2],
              ["zoom"],
              10,
              ["*", 5 / 2 - 1, ["^", 2, -6]],
              24,
              ["*", 5 / 2 - 1, ["^", 2, 5]],
            ],
          }}
          layout={{
            "line-join": "miter",
          }}
        />
      </Source>
      {currentArableFieldGeom &&
        fieldId &&
        fieldId !== editedFieldId &&
        tmpDeletedFeatures.indexOf(fieldId) === -1 && (
          <Source
            id="arable-field-highlight"
            data={currentArableFieldGeom as Feature}
            type="geojson"
          >
            <Layer
              id="arable-field-highlight"
              type="line"
              paint={{
                "line-color": "#FFFFFF",
                "line-width": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  10,
                  ["*", 5, ["^", 2, -6]],
                  24,
                  ["*", 5, ["^", 2, 7]],
                ],
                "line-offset": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  10,
                  ["*", 5 / 2 - 1, ["^", 2, -6]],
                  24,
                  ["*", 5 / 2 - 1, ["^", 2, 5]],
                ],
              }}
              layout={{
                "line-join": "miter",
              }}
            />
          </Source>
        )}
      {zoom &&
        zoom >= 13 &&
        !editedFieldId &&
        allFields.map((field: ArableField) => {
          return (
            <DiaryEntryLocationMarkerWithScroll
              setTooltipOverMarker={setTooltipOverMarker}
              key={`arable-diary-locarion-markers-${field.guid}`}
              zoom={zoom}
              currentField={field}
            />
          );
        })}

      {zoom &&
        zoom >= 15 &&
        arableFieldsWithNoTilesCenters.map((field) => {
          const { coordinates } = field.center.geometry;
          return (
            <Marker
              key={`arable-no-tiles-field-${field.guid}`}
              longitude={coordinates[0]}
              latitude={coordinates[1]}
            >
              <CircularProgress size={20} />
            </Marker>
          );
        })}
    </>
  );
}
